import React from 'react';

/**
 * RDCharacter
 *
 * Animated face built from the RetroDayz logo shapes (brick pattern,
 * ball, paddle). Renders differently depending on `state`:
 *
 *   - idle:      slow blink, nose bobs gently
 *   - listening: eyes widen slightly, no blink (focused on the user)
 *   - thinking:  nose bounces faster, mouth stays neutral/closed
 *   - talking:   mouth animates open/closed rapidly
 *
 * Usage:
 *   <RDCharacter state="idle" size={72} />
 */

const BRAND_BLUE = '#3E6D9C';
const BRAND_ORANGE = '#FD7014';

const BRICK_ROW_1 = [
  { x: 70, fill: BRAND_BLUE },
  { x: 94, fill: BRAND_BLUE },
  { x: 118, fill: BRAND_ORANGE },
  { x: 142, fill: BRAND_ORANGE },
  { x: 166, fill: BRAND_BLUE },
  { x: 190, fill: BRAND_BLUE },
];

const BRICK_ROW_2 = [
  { x: 70, fill: BRAND_ORANGE },
  { x: 94, fill: BRAND_ORANGE },
  { x: 118, fill: BRAND_BLUE },
  { x: 142, fill: BRAND_BLUE },
  { x: 166, fill: BRAND_ORANGE },
  { x: 190, fill: BRAND_ORANGE },
];

function Bricks() {
  return (
    <g>
      {BRICK_ROW_1.map((b) => (
        <rect key={`r1-${b.x}`} x={b.x} y={40} width={20} height={12} fill={b.fill} />
      ))}
      {BRICK_ROW_2.map((b) => (
        <rect key={`r2-${b.x}`} x={b.x} y={56} width={20} height={12} fill={b.fill} />
      ))}
    </g>
  );
}

function Eyes({ state }) {
  const isListening = state === 'listening';
  const isIdle = state === 'idle';

  // Listening: eyes stay open and slightly taller (attentive).
  // Idle: slow blink loop.
  // Thinking / talking: normal open eyes, no blink (focus stays on mouth/nose).
  const height = isListening ? 14 : 12;
  const y = isListening ? 79 : 80;

  return (
    <g>
      <rect x={118} y={y} width={20} height={height} rx={2} fill={BRAND_BLUE}>
        {isIdle && (
          <animate
            attributeName="height"
            values="12;12;1;12;12;12;12;1;12"
            dur="4s"
            repeatCount="indefinite"
            keyTimes="0;0.6;0.65;0.7;0.9;0.95;0.97;1;1"
          />
        )}
      </rect>
      <rect x={142} y={y} width={20} height={height} rx={2} fill={BRAND_BLUE}>
        {isIdle && (
          <animate
            attributeName="height"
            values="12;12;1;12;12;12;12;1;12"
            dur="4s"
            repeatCount="indefinite"
            keyTimes="0;0.6;0.65;0.7;0.9;0.95;0.97;1;1"
          />
        )}
      </rect>
    </g>
  );
}

function Nose({ state }) {
  const isThinking = state === 'thinking';
  const dur = isThinking ? '0.5s' : '1.4s';
  const values = isThinking ? '118;110;118' : '118;115;118';

  return (
    <circle cx={140} cy={118} r={9} fill="#8a8a8a">
      <animate attributeName="cy" values={values} dur={dur} repeatCount="indefinite" />
    </circle>
  );
}

function Mouth({ state }) {
  if (state === 'talking') {
    return (
      <rect x={110} y={150} width={60} height={10} fill="#2c2c2a">
        <animate
          attributeName="width"
          values="60;30;60;44;60"
          dur="0.5s"
          repeatCount="indefinite"
        />
        <animate attributeName="x" values="110;125;110;118;110" dur="0.5s" repeatCount="indefinite" />
      </rect>
    );
  }

  // Idle / listening / thinking: static closed mouth (subtle smile via rounded ends).
  return <rect x={110} y={150} width={60} height={8} rx={4} fill="#2c2c2a" />;
}

export default function RDCharacter({ state = 'idle', size = 72, className = '' }) {
  const label = {
    idle: 'RD, idle',
    listening: 'RD, listening',
    thinking: 'RD, thinking',
    talking: 'RD, talking',
  }[state] || 'RD';

  return (
    <svg
      viewBox="0 0 300 200"
      width={size}
      height={(size * 200) / 300}
      className={className}
      role="img"
      aria-label={label}
    >
      <Bricks />
      <Eyes state={state} />
      <Nose state={state} />
      <Mouth state={state} />
    </svg>
  );
}
